import { useState } from 'react';
import { DateTimeDisplay, StatusMessage, Breadcrumbs } from '../../components/ui';
import Modal from '../../components/ui/Modal';
import { ModalContent } from '../../components/ui/ModalContent';
import { DistributionGroupEditor } from '../../features/distribution/DistributionGroupEditor';
import { DistributionGroupList } from '../../features/distribution/DistributionGroupList';
import { useDistributionGroups } from '../../features/distribution/useDistributionGroups';
import type { DistributionGroupFormValues } from '../../features/distribution/distributionGroupRecipients';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import type { DistributionGroupDetail, DistributionGroupSummary } from '../../services/distribution/distribution.types';

const DistributionGroups = () => {
    const { groups, loading, submitting, error, success, loadForEdit, save, remove, removeRecipient, clearError, clearSuccess } = useDistributionGroups();
    const [editor, setEditor] = useState<{ group: DistributionGroupDetail | null } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<DistributionGroupSummary | null>(null);
    const breadcrumbs = useBreadcrumbs({ currentPageTitle: 'Distribution Groups' });

    const handleEdit = async (group: DistributionGroupSummary) => {
        const loadedGroup = await loadForEdit(group.id);
        if (loadedGroup) setEditor({ group: loadedGroup });
    };

    const handleSave = async (values: DistributionGroupFormValues) => {
        const saved = await save(values, editor?.group?.id);
        if (saved) setEditor(null);
        return saved;
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        setDeleteTarget(null);
        await remove(target.id);
    };

    const handleDeleteRecipient = async (recipientId: number) => {
        if (!editor?.group) throw new Error('No distribution group selected');
        await removeRecipient(editor.group.id, recipientId);
    };

    return (
        <main>
            <div className="container mb-2">
                <Modal
                    isOpen={deleteTarget !== null}
                    title="Delete Distribution Group"
                    headerClass="modal__header--danger"
                    content={<ModalContent heading="Delete Distribution Group" message="Are you sure you want to delete this distribution group?" />}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
                <section className="page">
                    <div className="heading__section">
                        <div className="heading__title-group">
                            <h2 className="heading__title heading__title--distribution-groups">Distribution Groups</h2>
                            <DateTimeDisplay />
                            <Breadcrumbs items={breadcrumbs} />
                        </div>
                        <div>
                            {!editor && (
                                <button type="button" className="btn btn-add-new" onClick={() => { clearError(); setEditor({ group: null }); }}>
                                    Add Distribution Group
                                </button>
                            )}
                        </div>
                    </div>
                </section>
                {!editor && error && <StatusMessage kind="error" onClose={clearError}>{error}</StatusMessage>}
                {!error && success && <StatusMessage kind="success" onClose={clearSuccess}>{success}</StatusMessage>}
                {submitting && <p>Saving distribution group…</p>}

                {editor && (
                    <DistributionGroupEditor
                        group={editor.group}
                        submitting={submitting}
                        error={error}
                        onDismissError={clearError}
                        onSave={handleSave}
                        onDeleteRecipient={handleDeleteRecipient}
                        onCancel={() => { if (!submitting) setEditor(null); }}
                    />
                )}
                <section className="page">
                    <div className="alarm-content">
                        <DistributionGroupList
                            groups={groups}
                            loading={loading}
                            editing={Boolean(editor?.group)}
                            onEdit={group => { void handleEdit(group); }}
                            onDelete={setDeleteTarget}
                        />
                    </div>
                </section>
            </div>
        </main>
    );
};

export default DistributionGroups;
